// A ticket's progress along its pipeline, small enough for a list row (#9304):
// a circle that fills like a pie as the ticket advances, and the percentage
// beside it. The ticket page's stepper is the full picture; this is the glance.
//
// Renders nothing for a ticket whose status is off its pipeline — a 0% would
// claim it is at the start, which is not where it is.
import { cn } from "@/lib/utils";
import { pipelineProgress } from "@/lib/pipelines";

interface PipelineProgressBadgeProps {
  name: string;
  stepStatusIds: readonly string[];
  statusId: string | null;
  className?: string;
}

// The pie is a stroke as wide as the circle's radius, dashed to the fraction:
// one element, no arc maths, and it scales with the font.
const R = 4;
const CIRCUMFERENCE = 2 * Math.PI * R;

const PipelineProgressBadge = ({ name, stepStatusIds, statusId, className }: PipelineProgressBadgeProps) => {
  const progress = pipelineProgress(stepStatusIds, statusId);
  if (!progress) return null;
  const { index, total, percent } = progress;
  const label = `${name}: step ${index + 1} of ${total}, ${percent}% complete`;

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn("flex shrink-0 items-center gap-1 text-xs tabular-nums text-muted-foreground", className)}
    >
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 -rotate-90" aria-hidden>
        <circle cx="8" cy="8" r="7" className="fill-none stroke-primary" strokeWidth="1.5" />
        <circle
          data-fill={percent}
          cx="8"
          cy="8"
          r={R}
          className="fill-none stroke-primary"
          strokeWidth={R * 2}
          strokeDasharray={`${(percent / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
        />
      </svg>
      <span aria-hidden>{percent}%</span>
    </span>
  );
};

export default PipelineProgressBadge;
